/**
 * PageHeader — Executive Principle 13 · Orientation Before Action.
 *
 * Every customer-facing page uses this single pattern so the owner
 * answers "Where am I?" and "Why am I here?" within 5 seconds.
 *
 *   [ Eyebrow ]
 *   Page Title
 *   One-sentence purpose.
 *   [ optional primary action ]
 *
 * Titles stay short and task-oriented. Subtitles carry the value.
 * No architectural / technical vocabulary — task-first only.
 */
import type { ReactNode } from "react";

interface PageHeaderProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  testId?: string;
  className?: string;
}

export function PageHeader({
  eyebrow, title, subtitle, action, testId, className,
}: PageHeaderProps): JSX.Element {
  return (
    <header
      data-testid={testId ?? "page-header"}
      className={`mb-8 flex flex-wrap items-start justify-between gap-4 ${className ?? ""}`}
    >
      <div className="min-w-0">
        {eyebrow ? (
          <p
            className="text-xs uppercase tracking-widest text-[var(--md-fog)]"
            data-testid="page-header-eyebrow"
          >
            {eyebrow}
          </p>
        ) : null}
        <h1
          data-testid="page-header-title"
          className="ds-headline ds-headline--h1 mt-2"
        >
          {title}
        </h1>
        {subtitle ? (
          <p
            className="mt-2 text-sm text-[var(--md-fog)] max-w-2xl"
            data-testid="page-header-subtitle"
          >
            {subtitle}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0" data-testid="page-header-action">{action}</div> : null}
    </header>
  );
}
